import axios from 'axios';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000/api';

const CSRF_COOKIE = 'csrf_token';
const CSRF_HEADER = 'X-CSRF-Token';

/**
 * Read csrf_token cookie. The backend sets a non-httpOnly csrf_token cookie alongside
 * the httpOnly auth cookies; we mirror it back in X-CSRF-Token on every state-changing
 * request. Backend middleware validates header == cookie (double-submit pattern).
 */
function getCsrfToken(): string | null {
  const match = document.cookie.match(new RegExp(`(?:^|;\\s*)${CSRF_COOKIE}=([^;]+)`));
  return match ? decodeURIComponent(match[1]) : null;
}

export const apiClient = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true, // send/receive httpOnly auth cookies
  headers: {
    'Content-Type': 'application/json',
  },
});

const refreshClient = axios.create({
  baseURL: API_BASE_URL,
  withCredentials: true,
  headers: {
    'Content-Type': 'application/json',
  },
});

const redirectToLogin = () => {
  if (window.location.pathname !== '/login') {
    window.location.href = '/login';
  }
};

const isAuthEndpoint = (url?: string) => {
  if (!url) return false;
  return url.includes('/auth/login') || url.includes('/auth/refresh');
};

// Attach CSRF token (double-submit) on state-changing requests.
// Применяется к ОБОИМ клиентам: иначе refreshClient слал POST /auth/refresh
// без X-CSRF-Token, бэкенд отвечал 403 CSRF mismatch и админка зацикливалась
// на 401 → refresh(403) → /login → 401 ... после протухания access-токена.
const attachCsrf = (config: any) => {
  const method = (config.method || 'get').toUpperCase();
  if (method !== 'GET' && method !== 'HEAD' && method !== 'OPTIONS') {
    const csrf = getCsrfToken();
    if (csrf) {
      config.headers[CSRF_HEADER] = csrf;
    }
  }
  return config;
};

apiClient.interceptors.request.use(attachCsrf, (error) => Promise.reject(error));
refreshClient.interceptors.request.use(attachCsrf, (error) => Promise.reject(error));

let refreshPromise: Promise<void> | null = null;

// Response interceptor: on 401, attempt one cookie-based refresh, then retry original request.
apiClient.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error.response?.status !== 401) {
      return Promise.reject(error);
    }

    const originalRequest = error.config as {
      _retry?: boolean;
      url?: string;
      headers?: Record<string, string>;
      method?: string;
    };
    if (!originalRequest) return Promise.reject(error);

    if (isAuthEndpoint(originalRequest.url)) {
      redirectToLogin();
      return Promise.reject(error);
    }

    if (originalRequest._retry) {
      redirectToLogin();
      return Promise.reject(error);
    }

    originalRequest._retry = true;

    if (!refreshPromise) {
      // Refresh uses cookies — backend reads admin_refresh_token cookie automatically.
      // Empty body is fine; backend falls back to cookie if body.refresh_token is missing.
      refreshPromise = refreshClient
        .post('/auth/refresh', {})
        .then(() => undefined)
        .finally(() => {
          refreshPromise = null;
        });
    }

    try {
      await refreshPromise;
      // After refresh, server has set new auth cookies. Replay original request — browser
      // attaches updated cookies automatically. Refresh CSRF header from new cookie.
      if (originalRequest.headers) {
        const csrf = getCsrfToken();
        const method = (originalRequest.method || 'get').toUpperCase();
        if (csrf && method !== 'GET' && method !== 'HEAD' && method !== 'OPTIONS') {
          originalRequest.headers[CSRF_HEADER] = csrf;
        }
      }
      return apiClient(originalRequest);
    } catch (refreshError) {
      redirectToLogin();
      return Promise.reject(refreshError);
    }
  },
);

export default apiClient;
